import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import CopyReport from "../ReportActionButtons/CopyReport";
import ResetReport from "../ReportActionButtons/ResetReport";
import AutoSelectedTab from "./AutoSelectedTab";
import MicToolComponent from "../../../StudyViewer/Components/Tools/ToolComponents/MicToolComponent";
import UserPreferenceWidget from "../ReportActionButtons/UserPreferenceWidget/UserPreferenceWidget";
import UserPreferenceWidgetIcon from "../../../../components/SVGComponents/UserPreferenceWidgetIcon";
import CustomToolTipHost from "../../../../components/CustomToolTipHost/CustomToolTipHost";
import SaveUserTemplate from "../ReportActionButtons/SaveUserTemplate";
import CapturedImagesButton from "../ReportActionButtons/CapturedImagesButton";
import ModalMain from "../../../../components/Modal/ModalMain";
import "./LLMTopBar.css";
import { iconButtonStyles } from "../../../../components/Modal/ModalHeader";
import AgenticReportingContext from "../../../../context/agentic-reporting/AgenticReportingContext";
import { Icon, Spinner, SpinnerSize } from "@fluentui/react";
import { REPORTDONESTATUS, USERTYPES } from "../../../../constants/Constants";
import userContext from "../../../../context/users/UserContext";

const LLMTopBar = ({ isChild }) => {
  const {
    confirmMultipleTemplateOpen,
    confirmMultipleTemplateModalContent,
    llmReportTitle,
    isWidget,
    autoSaveReportPreference,
    reportAutosaveStatus,
    reportAutosaveDirty,
    reportAutosaveLastSavedAt,
    reportDoneStatusState,
    snapshotRecoveryReopenVisible,
    handleSnapshotRecoveryReopen,
    alreadyLocked,
    reportAutosaveBlocked,
    patientReports,
    activeTab,
    activeTabKey,
  } = useContext(studyReportContext);

  const { inputValue, isFirstCall, isAgenticTab, selectedReportMention, isTalking } =
    useContext(AgenticReportingContext);

  const { roles, enableLlmReport } = useContext(userContext);
  const isRadiologist = roles.length > 0 && roles[0].includes(USERTYPES.RADIOLOGIST);
  const showLlmControls = isRadiologist && enableLlmReport;

  const [userPrefWidgetOpen, setUserPrefWidgetOpen] = useState(false);
  const [prefButtonRect, setPrefButtonRect] = useState(null);
  const prefButtonRef = useRef(null);



  // Close the preference widget whenever the user switches report tabs or Original/Addendum
  useEffect(() => {
    setUserPrefWidgetOpen(false);
  }, [activeTabKey, activeTab]);

  const isWidgetDisabled = useMemo(() => {
    return !isAgenticTab || !selectedReportMention || activeTab == 2;
  }, [isAgenticTab, selectedReportMention, activeTab]);

  const toggleWidget = () => {
    if (!userPrefWidgetOpen && prefButtonRef.current) {
      setPrefButtonRect(prefButtonRef.current.getBoundingClientRect());
    }
    setUserPrefWidgetOpen(!userPrefWidgetOpen);
  };

  const dynamicMargin = isWidget ? "0.3%" : "1.3%";
  const onlyUnreportedOrSaved =
        reportDoneStatusState === REPORTDONESTATUS.UNREPORTED ||
        reportDoneStatusState === REPORTDONESTATUS.SAVED ||
        reportDoneStatusState === REPORTDONESTATUS.DRAFT;

  const isSnapshotRecoveryOptionVisible =
    snapshotRecoveryReopenVisible && onlyUnreportedOrSaved && alreadyLocked;

  const isAutoSaveOptionVisible =
    autoSaveReportPreference &&
    onlyUnreportedOrSaved &&
    alreadyLocked &&
    !reportAutosaveBlocked;

  const hasReportTemplate = (patientReports?.length ?? 0) > 0;

  const getAutoSaveTooltipText = () => {
    if (snapshotRecoveryReopenVisible) return "Draft available";
    if (reportAutosaveStatus === "saving") return "Auto save: Saving draft";
    if (!hasReportTemplate) return "Auto save: On";
    if (
      reportAutosaveDirty &&
      (reportAutosaveStatus === "on" || reportAutosaveStatus === "saved")
    ) {
      return "Auto save: Unsaved changes";
    }
    if (reportAutosaveStatus === "saved") return "Auto save: Draft saved";
    return reportAutosaveLastSavedAt ? "Auto save: Draft saved" : "Auto save: On";
  };

  return (
    <>
      <div
        className="llm-tab-container"
        style={{ paddingTop: isWidget ? "4px" : "8px" }}
      >
        <div className="llm-tab-wrapper">
          <div
            className="llm-tab-content"
            style={{ marginLeft: dynamicMargin }}
          >
            <AutoSelectedTab isChild={isChild} />
          </div>
          {showLlmControls && <div className="llm-controls" style={{ marginRight: dynamicMargin }}>
            <MicToolComponent />
            <CopyReport />
            <ResetReport />
            <SaveUserTemplate />
            <CapturedImagesButton />

            {(isSnapshotRecoveryOptionVisible || isAutoSaveOptionVisible) && (
              snapshotRecoveryReopenVisible ? (
                <CustomToolTipHost content={getAutoSaveTooltipText()} id="llm-autosave-status">
                  <span
                    onClick={handleSnapshotRecoveryReopen}
                    className="highlight-tool-llm"
                    styles={iconButtonStyles}
                    style={{ marginTop: "2px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    //style={{ fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer" }}
                  >
                    <Icon iconName="Save" style={{ color: "#0078d4", fontSize: "14px" }} />
                  </span>
                </CustomToolTipHost>
              ) : (
                <CustomToolTipHost content={getAutoSaveTooltipText()} id="llm-autosave-status">
                  <div
                    className="highlight-tool-llm"
                    styles={iconButtonStyles}
                    style={{ marginTop: "2px", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    {reportAutosaveStatus === "saving" ? (
                      <Spinner size={SpinnerSize.xSmall} />
                    ) : hasReportTemplate &&
                      reportAutosaveDirty &&
                      (reportAutosaveStatus === "on" ||
                        reportAutosaveStatus === "saved") ? (
                      <Icon iconName="Save" style={{ color: "#d29200", fontSize: "14px" }} />
                    ) : (
                      <Icon
                        iconName={reportAutosaveLastSavedAt ? "CompletedSolid" : "SkypeCircleCheck"}
                        style={{ color: "#107c10", fontSize: "14px" }}
                      />
                    )}
                  </div>
                </CustomToolTipHost>
              )
            )}
            <CustomToolTipHost content={userPrefWidgetOpen ? undefined : "Manage Preferences"} id="Preference">
              <div
                ref={prefButtonRef}
                className="highlight-tool-llm"
                styles={iconButtonStyles}
                style={{
                  cursor: isWidgetDisabled ? "not-allowed" : "pointer",
                  marginTop: "2px",
                  opacity: isWidgetDisabled ? 0.5 : 1,
                }}
                onClick={() => {
                  if (!isWidgetDisabled) toggleWidget();
                }}
              >
                <UserPreferenceWidgetIcon />
              </div>
            </CustomToolTipHost>
            {isAgenticTab && userPrefWidgetOpen && prefButtonRect && (
              <UserPreferenceWidget
                isOpen={userPrefWidgetOpen}
                onClose={() => setUserPrefWidgetOpen(false)}
                anchorRect={prefButtonRect}
              />
            )}
          </div>}
        </div>
      </div>

      {confirmMultipleTemplateOpen && (
        <ModalMain
          updatedModal={true}
          modalDetails={confirmMultipleTemplateModalContent}
          showModal={true}
        />
      )}
    </>
  );
};

export default LLMTopBar;
